import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { ArrowUpDown, ChevronDown, ChevronUp } from "lucide-react";

import styles from "./table.module.css";

import type { SortDirection } from "./types.ts";

export type TableProps = ComponentPropsWithoutRef<"table">;

export const Table = forwardRef<HTMLTableElement, TableProps>(
  function Table({ className, children, ...rest }, ref) {
    return (
      <div className={styles.wrapper}>
        <table
          ref={ref}
          className={clsx(styles.table, className)}
          {...rest}
        >
          {children}
        </table>
      </div>
    );
  }
);

export type TableHeaderProps = ComponentPropsWithoutRef<"thead">;

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(function TableHeader({ className, children, ...rest }, ref) {
  return (
    <thead ref={ref} className={clsx(styles.thead, className)} {...rest}>
      {children}
    </thead>
  );
});

export type TableBodyProps = ComponentPropsWithoutRef<"tbody">;

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  TableBodyProps
>(function TableBody({ className, children, ...rest }, ref) {
  return (
    <tbody ref={ref} className={clsx(styles.tbody, className)} {...rest}>
      {children}
    </tbody>
  );
});

export type TableFooterProps = ComponentPropsWithoutRef<"tfoot">;

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  TableBodyProps
>(function TableFooter({ className, children, ...rest }, ref) {
  return (
    <tfoot ref={ref} className={clsx(styles.tfoot, className)} {...rest}>
      {children}
    </tfoot>
  );
});

export type TableRowProps = ComponentPropsWithoutRef<"tr">;

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  function TableRow({ className, children, ...rest }, ref) {
    return (
      <tr ref={ref} className={clsx(styles.tr, className)} {...rest}>
        {children}
      </tr>
    );
  }
);

export type TableHeadCellProps = ComponentPropsWithoutRef<"th">;

export const TableHeadCell = forwardRef<
  HTMLTableCellElement,
  TableHeadCellProps
>(function TableHeadCell({ className, children, ...rest }, ref) {
  return (
    <th
      ref={ref}
      scope="col"
      className={clsx(
        styles.th,
        "heading-xs",
        styles.headerContent,
        className
      )}
      {...rest}
    >
      {children}
    </th>
  );
});

export type TableCellProps = ComponentPropsWithoutRef<"td">;

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  function TableCell({ className, children, ...rest }, ref) {
    return (
      <td
        ref={ref}
        className={clsx(
          styles.td,
          "body-sm",
          styles.cellContent,
          className
        )}
        {...rest}
      >
        {children}
      </td>
    );
  }
);

export type SortableHeadCellProps = Omit<
  TableHeadCellProps,
  "children" | "scope"
> & {
  columnId: string;
  children: ReactNode;
  sortable?: boolean;
  sortDirection: SortDirection;
  onSort?: () => void;
};

export const SortableHeadCell = forwardRef<
  HTMLTableCellElement,
  SortableHeadCellProps
>(function SortableHeadCell(
  {
    columnId,
    children,
    sortable = false,
    sortDirection,
    onSort,
    className,
    ...rest
  },
  ref
) {
  const ariaSort =
    sortDirection === "asc"
      ? "ascending"
      : sortDirection === "desc"
        ? "descending"
        : "none";

  if (!sortable) {
    return (
      <th
        ref={ref}
        id={columnId}
        data-column-id={columnId}
        scope="col"
        className={clsx(
          styles.th,
          "heading-xs",
          styles.headerContent,
          className
        )}
        {...rest}
      >
        {children}
      </th>
    );
  }

  return (
    <th
      ref={ref}
      id={columnId}
      data-column-id={columnId}
      scope="col"
      aria-sort={ariaSort}
      className={clsx(
        styles.th,
        "heading-xs",
        styles.headerContent,
        className
      )}
      {...rest}
    >
      <button
        type="button"
        className={clsx(styles.sortButton, styles.headerContent)}
        onClick={onSort}
      >
        <span className="heading-xs">{children}</span>
        <span className={styles.sortIcon} aria-hidden>
          {sortDirection === "asc" ? (
            <ChevronUp size={14} strokeWidth={2} />
          ) : sortDirection === "desc" ? (
            <ChevronDown size={14} strokeWidth={2} />
          ) : (
            <ArrowUpDown size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    </th>
  );
});
